import { useMemo } from 'react'
import useOffers from 'hooks/Offers/useOffers'
import useOfferList from 'hooks/Offers/useOfferList'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import Places from 'constants/places'
import type { FlightsClassTypes } from 'constants/flight'
import { HOTEL_OFFERS_LIMIT } from 'constants/charterFlight'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { useMaldivesOffersPricing } from './useMaldivesOffersPricing'
import type { CalendarItemBundleData } from './types'

interface UseCheapestBundlePriceParams {
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  disabled?: boolean
}

/**
 * Fetches all charter bundle offers via a non-stream (light) API request,
 * then uses calendar + flight data to compute the lowest bundle price
 * for a specific check-in date across all offers.
 *
 * Unlike useCalendarItemData (stream-based), this hook:
 * - Uses isStream: false to get all offers in one request
 * - Uses calendar API for date-specific pricing (not base package price)
 * - Computes prices via the same useMaldivesOffersPricing logic as the landing page
 */
export function useCheapestBundlePrice(params: UseCheapestBundlePriceParams): CalendarItemBundleData {
  const {
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    charterBundleRoute,
    disabled = false,
  } = params

  const lightFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (disabled) {
      return undefined
    }
    const filters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: '', // Light search ignores these anyway
      checkOut: '', // Light search ignores these anyway
      rooms,
    })
    return {
      ...filters,
      isStream: false,
      limit: HOTEL_OFFERS_LIMIT,
    }
  }, [disabled, rooms])

  const offerList = useOfferList(lightFilters, {
    minRequiredOffers: HOTEL_OFFERS_LIMIT,
    disabled: disabled || !lightFilters,
  })

  const [offers, offersFetching] = useOffers<App.HotelOffer>(offerList.offerIds)

  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: disabled || !charterBundleRoute,
  })

  const offersPricingData = useMaldivesOffersPricing({
    offers,
    rooms,
    dateRangesByMonth,
    searchFareClass: fareClass,
    charterBundleRoute,
    selectedCheckIn: checkIn || undefined,
    selectedCheckOut: checkOut || undefined,
    nights,
  })

  const bundleData = useMemo<CalendarItemBundleData>(() => {
    const isLoading = offerList.fetching || offersFetching || offersPricingData.isLoading

    if (disabled || !checkIn) {
      return { fetching: false, available: false, totalPrice: 0, pricePerPerson: 0, offerCount: 0 }
    }

    const calendarEntry = offersPricingData.calendarPricingByDate[checkIn]

    if (!calendarEntry) {
      return { fetching: isLoading, available: false, totalPrice: 0, pricePerPerson: 0, offerCount: 0 }
    }

    return calendarEntry
  }, [disabled, checkIn, offerList.fetching, offersFetching, offersPricingData])

  return bundleData
}
